import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Download, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ name: '', doc_type: 'BEE', expiry_date: '', file: null });

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const { data } = await axios.get(`${API}/documents`, { withCredentials: true });
      setDocuments(data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', formData.file);
      data.append('name', formData.name);
      data.append('doc_type', formData.doc_type);
      if (formData.expiry_date) {
        data.append('expiry_date', formData.expiry_date);
      }

      await axios.post(`${API}/documents/upload`, data, { withCredentials: true });
      toast.success('Document uploaded successfully');
      setShowDialog(false);
      setFormData({ name: '', doc_type: 'BEE', expiry_date: '', file: null });
      loadDocuments();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const { data } = await axios.get(`${API}/documents/${doc.id}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API}/documents/${id}`, { withCredentials: true });
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="documents-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="documents-title">
            Documents
          </h1>
          <p className="mt-2 text-zinc-600">Manage your business documents</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-black text-white hover:bg-zinc-800 gap-2" data-testid="upload-document-button">
              <Plus size={18} /> Upload Document
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Upload Document</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Document Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="document-name-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                />
              </div>
              <div>
                <Label htmlFor="doc_type" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Document Type</Label>
                <Select value={formData.doc_type} onValueChange={(value) => setFormData({...formData, doc_type: value})}>
                  <SelectTrigger className="mt-2" data-testid="document-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BEE">BEE Certificate</SelectItem>
                    <SelectItem value="Tax">Tax Certificate</SelectItem>
                    <SelectItem value="CIPC">CIPC Document</SelectItem>
                    <SelectItem value="Contract">Contract</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="expiry_date" className="text-xs font-bold uppercase tracking-wider text-zinc-600">Expiry Date (Optional)</Label>
                <Input
                  id="expiry_date"
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                  data-testid="document-expiry-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                />
              </div>
              <div>
                <Label htmlFor="file" className="text-xs font-bold uppercase tracking-wider text-zinc-600">File</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                  required
                  data-testid="document-file-input"
                  className="mt-2 border-zinc-300 hover:border-black transition-colors"
                />
              </div>
              <Button type="submit" disabled={uploading} className="w-full bg-black text-white hover:bg-zinc-800" data-testid="submit-document-button">
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="documents-table">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Name</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Type</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Expiry Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Uploaded</th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-zinc-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {documents.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-zinc-500" data-testid="no-documents">
                    No documents yet. Upload your first document to get started.
                  </td>
                </tr>
              ) : (
                documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-zinc-50 transition-colors" data-testid={`document-row-${doc.id}`}>
                    <td className="px-6 py-4 font-medium text-zinc-900">{doc.name}</td>
                    <td className="px-6 py-4 text-zinc-600 text-sm">{doc.type}</td>
                    <td className="px-6 py-4 text-zinc-600 text-sm">
                      {doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-zinc-600 text-sm">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleDownload(doc)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          data-testid={`download-document-${doc.id}`}
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="text-red-600 hover:text-red-800 transition-colors"
                          data-testid={`delete-document-${doc.id}`}
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
